import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Modal from "@/components/base/Modal";
import { useSession } from "@/session/SessionProvider";

export default function CompteSection() {
  const navigate = useNavigate();
  const { logout } = useSession();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [notice, setNotice] = useState("");

  const handleExport = () => {
    setNotice("L'export de vos données sera disponible dès que le stockage de votre espace sera activé.");
  };

  const handleDelete = () => {
    setDeleteOpen(false);
    setNotice("La suppression définitive du compte sera disponible dès que la connexion sécurisée sera activée.");
  };

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
        <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
            <i className="ri-account-circle-line text-base" aria-hidden="true" />
          </span>
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Gestion du compte</h3>
            <p className="text-xs text-foreground-500">Export de vos données et gestion de votre compte d'agence.</p>
          </div>
        </header>

        <div className="flex flex-col gap-4 p-4 md:p-5">
          <div className="flex flex-col gap-3 rounded-md border border-background-200 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-label font-medium text-foreground-900">Exporter les données de l'agence</p>
              <p className="mt-0.5 text-xs text-foreground-500">
                Récupérez vos propriétaires, immeubles, biens, locataires, contrats et employés.
              </p>
            </div>
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-download-2-line text-base" aria-hidden="true" />
              Exporter
            </button>
          </div>

          <div className="flex flex-col gap-3 rounded-md border border-primary-200 bg-primary-50/40 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-label font-medium text-primary-900">Supprimer le compte de l'agence</p>
              <p className="mt-0.5 text-xs text-foreground-600">
                Cette action est définitive et supprime toutes les données de l'agence.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
            >
              <i className="ri-delete-bin-line text-base" aria-hidden="true" />
              Supprimer
            </button>
          </div>

          {notice && (
            <p className="flex items-start gap-2 rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5 text-xs text-foreground-600">
              <i className="ri-information-line mt-0.5 text-sm text-foreground-500" aria-hidden="true" />
              {notice}
            </p>
          )}

          <Link
            to="/agence/abonnement"
            className="flex items-center justify-between gap-3 rounded-md border border-background-200 px-4 py-3 transition-colors hover:bg-background-100 cursor-pointer"
          >
            <span className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                <i className="ri-vip-diamond-line text-base" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-label font-medium text-foreground-950">Gérer l'abonnement</span>
                <span className="block text-xs text-foreground-500">Formule, limites et renouvellement.</span>
              </span>
            </span>
            <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
        <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
            <i className="ri-logout-box-r-line text-base" aria-hidden="true" />
          </span>
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Session</h3>
            <p className="text-xs text-foreground-500">Déconnectez-vous de l'espace Agence.</p>
          </div>
        </header>
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between md:p-5">
          <p className="text-xs text-foreground-500">
            Vous devrez vous reconnecter pour accéder de nouveau au tableau de bord de l'agence.
          </p>
          <button
            type="button"
            onClick={handleLogout}
            className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-logout-box-r-line text-base" aria-hidden="true" />
            Déconnexion
          </button>
        </div>
      </section>

      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        size="sm"
        title="Supprimer le compte de l'agence"
        description="Voulez-vous vraiment supprimer le compte de l'agence ? Cette action est définitive et entraîne la perte de toutes les données."
      >
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => setDeleteOpen(false)}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-delete-bin-line text-base" aria-hidden="true" />
            Supprimer
          </button>
        </div>
      </Modal>
    </div>
  );
}